import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, BadgeCheck, Award } from "lucide-react";
import { Spinner } from "@/components/site/ProtectedRoute";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const SITE = "https://digitalnomadnicaragua.com";
const longDate = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export function CertificateSheet({ cert, sample = false }) {
  const NameTag = sample ? "p" : "h1";
  const CourseTag = sample ? "p" : "h2";
  return (
    <div className="cert-sheet relative bg-[#FFFCF6] border-[6px] sm:border-[10px] border-double border-[#0B2A4A] rounded-xl px-5 py-8 sm:p-14 text-center flex flex-col justify-between gap-6 sm:gap-8 overflow-hidden" data-testid={sample ? "certificate-sample" : "certificate-sheet"}>
      {sample && <span className="absolute inset-0 flex items-center justify-center pointer-events-none font-display text-6xl sm:text-8xl font-bold text-[#0B2A4A]/[0.06] -rotate-12">SAMPLE</span>}
      <div className="absolute inset-3 border border-[#F08A3C]/50 rounded-lg pointer-events-none" />
      <img src="/brand/logo-horizontal.jpg" alt="Digital Nomad Nicaragua" className="h-12 sm:h-16 mx-auto object-contain" />
      <div>
        <p className="font-mono-ui text-[10px] sm:text-xs tracking-[0.4em] text-[#147C88]">CERTIFICATE OF COMPLETION</p>
        <p className="mt-4 text-sm text-slate-600">This certifies that</p>
        <NameTag className="mt-2 font-display text-3xl sm:text-5xl font-bold text-[#0B2A4A]" data-testid="certificate-name">{cert.name}</NameTag>
        <p className="mt-4 text-sm text-slate-600">has successfully completed the</p>
        <CourseTag className="mt-1 font-display text-xl sm:text-2xl font-semibold text-[#D96F28]" data-testid="certificate-course">{cert.course}</CourseTag>
      </div>
      <div className="grid grid-cols-2 gap-6 items-end text-xs sm:text-sm">
        <div><p className="font-semibold text-[#0B2A4A]" data-testid="certificate-date">{longDate(cert.issued_at)}</p><p className="border-t border-[#0B2A4A]/40 mt-1 pt-1 text-slate-500">Date</p></div>
        <div><p className="text-4xl sm:text-5xl leading-none text-[#0B2A4A] -mb-1" style={{ fontFamily: "'Mrs Saint Delafield', cursive" }} data-testid="certificate-signature">{cert.issued_by}</p><p className="border-t border-[#0B2A4A]/40 mt-1 pt-1 text-slate-500">Greg Miceli, Instructor</p></div>
      </div>
      <p className="text-[9px] sm:text-[10px] text-slate-500 font-mono-ui break-all" data-testid="certificate-id">Certificate ID {cert.id} · Verify at {SITE}/verify/{cert.id}</p>
    </div>
  );
}

export function SampleCertificate({ course, how }) {
  const cert = { id: "SAMPLE-0000", name: "Your Name Here", course, issued_by: "Greg Miceli", issued_at: new Date().toISOString() };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-[1fr_1.3fr] gap-10 items-center" data-testid="sample-certificate-section">
      <div>
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">YOUR CERTIFICATE</p>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Earned, not given</h2>
        <p className="mt-3 text-slate-600 leading-relaxed">{how}</p>
        <ul className="mt-5 flex flex-col gap-2 text-sm text-slate-700">
          <li className="flex gap-2"><BadgeCheck className="h-5 w-5 text-[#1A9AA8] shrink-0" /> Signed by Greg Miceli with your name and completion date</li>
          <li className="flex gap-2"><BadgeCheck className="h-5 w-5 text-[#1A9AA8] shrink-0" /> A unique certificate ID anyone can verify on this website</li>
          <li className="flex gap-2"><BadgeCheck className="h-5 w-5 text-[#1A9AA8] shrink-0" /> Print it or save it as a PDF from your dashboard</li>
        </ul>
      </div>
      <CertificateSheet cert={cert} sample />
    </section>
  );
}

export default function CertificatePage({ verify = false }) {
  const { id } = useParams();
  useSeo({ title: verify ? "Verify certificate" : "Certificate", path: `/${verify ? "verify" : "certificate"}/${id}`, noindex: true });
  const [cert, setCert] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(verify ? `/certificates/verify/${id}` : `/certificates/${id}`).then((r) => setCert(r.data)).catch(() => setError("No certificate found with this ID."));
  }, [id, verify]);
  if (error) return <p className="text-center py-24 text-rose-600" data-testid="certificate-error">{error}</p>;
  if (!cert) return <Spinner />;
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10 flex flex-col gap-6">
      {verify ? (
        <p className="no-print inline-flex items-center gap-2 self-start rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 text-sm font-semibold" data-testid="certificate-verified"><BadgeCheck className="h-5 w-5" /> Verified — this certificate was issued by Digital Nomad Nicaragua.</p>
      ) : (
        <div className="no-print flex flex-wrap gap-2">
          <Link to="/dashboard" className="rounded-xl border border-[#0B2A4A]/20 px-4 py-2 text-sm font-semibold">Back</Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-4 py-2 text-sm font-semibold" data-testid="certificate-print-btn"><Printer className="h-4 w-4" /> Print or save as PDF</button>
        </div>
      )}
      <CertificateSheet cert={cert} />
    </section>
  );
}

export function CertificateLinks({ certs }) {
  if (!certs?.length) return null;
  return (
    <div className="bg-white rounded-3xl border border-[#F08A3C]/40 p-6 flex flex-col gap-3" data-testid="my-certificates">
      <h2 className="flex items-center gap-2 text-xl font-bold text-[#0B2A4A]"><Award className="h-5 w-5 text-[#F08A3C]" /> Your certificates</h2>
      {certs.map((c) => (
        <Link key={c.id} to={`/certificate/${c.id}`} className="flex flex-wrap justify-between gap-2 rounded-xl bg-[#FAF6EE] px-4 py-3 text-sm font-semibold text-[#0B2A4A] hover:bg-[#F5ECD7]" data-testid={`certificate-link-${c.id}`}>
          <span>{c.course}</span><span className="text-[#F08A3C]">View, print or download</span>
        </Link>
      ))}
    </div>
  );
}
